import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, Loader2, AlertTriangle, RefreshCw, FileText, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface PendingAcceptance {
  pageSlug: string;
  versionNumber: number;
  changeDescription: string | null;
  pageTitle: string;
}

interface PageData {
  content: string | null;
  title: string;
}

function AcceptanceModal({ pending, total, index, onAccepted }: {
  pending: PendingAcceptance;
  total: number;
  index: number;
  onAccepted: () => void;
}) {
  const { toast } = useToast();
  const [checked, setChecked] = useState(false);

  const { data: pageData, isLoading: contentLoading } = useQuery<PageData>({
    queryKey: ["/api/pages/slug", pending.pageSlug],
    queryFn: async () => {
      const res = await fetch(`/api/pages/slug/${pending.pageSlug}`);
      if (!res.ok) throw new Error("Could not load document");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/legal/acceptances", {
        pageSlug: pending.pageSlug,
        versionNumber: pending.versionNumber,
      });
    },
    onSuccess: () => {
      onAccepted();
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Failed to record acceptance",
        description: "Please try again.",
      });
    },
  });

  return (
    <Dialog open modal>
      <DialogContent
        className="max-w-2xl w-full flex flex-col gap-0 p-0 max-h-[90vh] sm:max-h-[85vh] overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        data-testid="modal-reacceptance"
      >
        {/* ── Header ── */}
        <div className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <DialogTitle className="text-base font-semibold leading-tight" data-testid="modal-title">
                  Updated {pending.pageTitle}
                </DialogTitle>
                {total > 1 && (
                  <span className="shrink-0 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {index + 1} of {total}
                  </span>
                )}
              </div>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                Version {pending.versionNumber} — please read and accept to continue.
              </DialogDescription>
            </div>
          </div>

          {/* What changed summary */}
          {pending.changeDescription && (
            <div className="mt-3 flex gap-2 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 px-3 py-2.5">
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">What's changed</p>
                <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">{pending.changeDescription}</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Scrollable document content ── */}
        <div className="flex-1 overflow-y-auto min-h-0" data-testid="modal-doc-scroll">
          {contentLoading ? (
            <div className="p-6 space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ) : pageData?.content ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none px-6 py-5 [&_p]:mb-3 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_a]:text-primary [&_a]:underline [&_table]:w-full [&_th]:text-left [&_th]:p-2 [&_td]:p-2 [&_table]:border-collapse [&_th]:border [&_td]:border"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
              data-testid="modal-doc-content"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center px-6">
              <FileText className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                Document content could not be loaded.{" "}
                <a
                  href={`/page/${pending.pageSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Open in new tab
                </a>
              </p>
            </div>
          )}
        </div>

        {/* ── Sticky footer ── */}
        <div className="px-6 py-4 border-t bg-muted/30 shrink-0 space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="accept-updated"
              checked={checked}
              onCheckedChange={v => setChecked(v === true)}
              data-testid="checkbox-accept-update"
              className="mt-0.5"
            />
            <label htmlFor="accept-updated" className="text-sm leading-snug cursor-pointer select-none">
              I have read and agree to the <strong>{pending.pageTitle}</strong> (Version {pending.versionNumber}).
            </label>
          </div>

          <Button
            disabled={!checked || acceptMutation.isPending}
            onClick={() => acceptMutation.mutate()}
            className="w-full"
            data-testid="button-confirm-acceptance"
          >
            {acceptMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="mr-2 h-4 w-4" />
            )}
            Accept and continue
            {total > 1 && index + 1 < total && (
              <ChevronRight className="ml-1 h-4 w-4" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GateErrorBlock() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur">
      <div className="max-w-sm text-center space-y-4 p-6 border rounded-xl bg-background shadow-lg">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <h2 className="text-lg font-semibold">Could not load required agreements</h2>
        <p className="text-sm text-muted-foreground">
          We were unable to verify your acceptance of our legal documents. Please refresh to try again.
        </p>
        <Button onClick={() => window.location.reload()} data-testid="button-retry-acceptance-check">
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    </div>
  );
}

export function LegalAcceptanceGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: pending, isLoading: pendingLoading, isError: pendingError } = useQuery<PendingAcceptance[]>({
    queryKey: ["/api/legal/pending-acceptances"],
    queryFn: async () => {
      const res = await fetch("/api/legal/pending-acceptances", { credentials: "include" });
      if (!res.ok) throw new Error(`Pending acceptances check failed: ${res.status}`);
      return res.json();
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  useEffect(() => {
    setCurrentIndex(0);
  }, [pending]);

  const handleAccepted = () => {
    const next = currentIndex + 1;
    if (pending && next < pending.length) {
      setCurrentIndex(next);
    } else {
      queryClient.setQueryData(["/api/legal/pending-acceptances"], []);
    }
  };

  if (isAuthenticated && !authLoading && !pendingLoading && pendingError) {
    return <GateErrorBlock />;
  }

  const activePending = pending ?? [];
  const showingModal =
    isAuthenticated &&
    !authLoading &&
    !pendingLoading &&
    !pendingError &&
    activePending.length > 0 &&
    currentIndex < activePending.length;

  return (
    <>
      {showingModal && (
        <AcceptanceModal
          pending={activePending[currentIndex]}
          total={activePending.length}
          index={currentIndex}
          onAccepted={handleAccepted}
        />
      )}
      {children}
    </>
  );
}
